/* PortfolioBenchmarkHeatmap — parses the markdown tables in the
 * portfolio_benchmark section and renders a colour-coded heatmap.
 *
 * Parsing strategy:
 *   1. Find any markdown table whose cell values are mostly numeric (1-10).
 *   2. First column = dimension label, remaining columns = peer countries.
 *   3. Render as a heatmap grid (rows = dimensions, cols = countries).
 *   4. Column totals row shows the composite score per country.
 *
 * Graceful fallback: if no numeric table found, component renders nothing.
 */
import React, { useMemo } from 'react';
import { BarChart3, AlertCircle } from 'lucide-react';

// Colour ramp for 0-10 scoring — viridis-like for readability
function heatmapColor(v) {
  if (v === null || v === undefined || isNaN(v)) return '#e2e8f0';
  const t = Math.max(0, Math.min(1, v / 10));
  if (t < 0.25) return `rgba(239, 68, 68, ${0.35 + t * 1.2})`;         // rose — bad
  if (t < 0.50) return `rgba(251, 146, 60, ${0.35 + (t - 0.25) * 1.5})`;// orange
  if (t < 0.70) return `rgba(251, 191, 36, ${0.50 + (t - 0.50) * 1.4})`;// amber
  if (t < 0.85) return `rgba(132, 204, 22, ${0.55 + (t - 0.70) * 1.5})`;// lime
  return `rgba(16, 185, 129, ${0.65 + (t - 0.85) * 2.0})`;              // emerald — good
}

function textColorFor(v) {
  if (v === null || v === undefined || isNaN(v)) return '#94a3b8';
  return v >= 3 ? '#0b1c30' : 'white';
}

function parseBenchmarkTable(markdown = '') {
  if (!markdown || typeof markdown !== 'string') return null;
  const lines = markdown.split('\n');
  let best = null;

  let inTable = false;
  let header = null;
  let rows = [];

  const commitTable = () => {
    if (!header || rows.length < 3) return;
    // A benchmark table needs ≥3 countries (cols minus first) and ≥3 dimensions (rows).
    if (header.length < 4) return;
    // At least 60% of row body cells should be numeric.
    const bodyCells = rows.flatMap(r => r.slice(1));
    const numCount = bodyCells.filter(c => /^\s*\d+(\.\d+)?\s*$/.test(c)).length;
    if (numCount / bodyCells.length < 0.5) return;

    const numRows = rows.map(r => ({
      label: r[0].replace(/\*\*/g, '').trim(),
      values: r.slice(1).map(c => {
        const m = String(c).match(/(-?\d+(?:\.\d+)?)/);
        return m ? parseFloat(m[1]) : null;
      }),
    }));

    const candidate = {
      countries: header.slice(1).map(h => h.replace(/\*\*/g, '').trim()),
      rows: numRows,
      totalNumeric: numCount,
    };
    if (!best || candidate.totalNumeric > best.totalNumeric) {
      best = candidate;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line.startsWith('|')) {
      commitTable();
      inTable = false; header = null; rows = [];
      continue;
    }
    const cells = line.split('|').map(c => c.trim()).filter((_, idx, arr) => idx !== 0 && idx !== arr.length - 1);
    if (cells.every(c => /^:?-+:?$/.test(c))) continue; // separator row

    if (!inTable) {
      header = cells;
      inTable = true;
      rows = [];
    } else {
      rows.push(cells);
    }
  }
  commitTable();
  return best;
}

export default function PortfolioBenchmarkHeatmap({ markdown, darkMode = false }) {
  const data = useMemo(() => parseBenchmarkTable(markdown), [markdown]);

  if (!data || data.rows.length < 3 || data.countries.length < 3) {
    return null;
  }

  // Compute composite (column mean of non-null cells)
  const composite = data.countries.map((_, cIdx) => {
    const vals = data.rows.map(r => r.values[cIdx]).filter(v => v !== null && !isNaN(v));
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  });

  // Rank columns by composite desc
  const ranked = [...composite.map((v, i) => ({ i, v }))].sort((a, b) => (b.v ?? -1) - (a.v ?? -1));
  const tierOf = (rank) => {
    if (rank === 0) return { label: 'TIER 1', tone: 'bg-emerald-500 text-white' };
    if (rank <= 2)  return { label: 'TIER 2', tone: 'bg-amber-500 text-white' };
    return { label: 'TIER 3', tone: 'bg-slate-500 text-white' };
  };
  const rankMap = {};
  ranked.forEach((r, rk) => { rankMap[r.i] = rk; });

  return (
    <div
      data-testid="portfolio-heatmap"
      className={`my-6 rounded-xl overflow-hidden border ${darkMode ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'} shadow-sm`}
    >
      <div className="px-5 pt-4 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#0891b2] to-[#10b981] flex items-center justify-center">
            <BarChart3 className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className={`text-[10px] font-black uppercase tracking-[0.2em] ${darkMode ? 'text-white/50' : 'text-slate-500'}`}>
              Portfolio Benchmark · Auto-extracted
            </p>
            <p className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              {data.countries.length} peer countries · {data.rows.length} benchmark dimensions
            </p>
          </div>
        </div>
        <span className={`text-[10px] ${darkMode ? 'text-white/40' : 'text-slate-400'}`}>parsed from the benchmark table below</span>
      </div>

      <div className="p-5 pt-1 overflow-x-auto">
        <table className="w-full border-collapse text-[11.5px] min-w-[680px]">
          <thead>
            <tr>
              <th className={`text-left p-2 border-b ${darkMode ? 'text-white/60 border-white/10' : 'text-slate-500 border-slate-200'} font-black uppercase tracking-wide text-[10px] w-[34%]`}>
                Dimension
              </th>
              {data.countries.map((c, i) => (
                <th key={i} className={`p-2 border-b text-center ${darkMode ? 'text-white border-white/10' : 'text-slate-900 border-slate-200'} font-bold`}>
                  <div className="flex flex-col items-center gap-0.5">
                    <span>{c}</span>
                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded ${tierOf(rankMap[i]).tone}`}>
                      {tierOf(rankMap[i]).label}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, rIdx) => (
              <tr key={rIdx}>
                <td className={`p-2 border-b ${darkMode ? 'text-white/80 border-white/10' : 'text-slate-700 border-slate-100'} font-semibold`}>
                  {row.label}
                </td>
                {row.values.map((v, cIdx) => (
                  <td key={cIdx}
                    className="p-1.5 border-b border-slate-100 text-center font-bold"
                    style={{ background: heatmapColor(v), color: textColorFor(v) }}
                    data-testid={`heatmap-cell-${rIdx}-${cIdx}`}
                  >
                    {v !== null && v !== undefined ? v.toFixed(1) : '—'}
                  </td>
                ))}
              </tr>
            ))}
            {/* Composite row */}
            <tr>
              <td className={`p-2 border-t-2 ${darkMode ? 'text-white border-white/30 bg-white/5' : 'text-slate-900 border-slate-400 bg-slate-50'} font-black uppercase tracking-wide text-[10.5px]`}>
                COMPOSITE
              </td>
              {composite.map((v, i) => (
                <td key={i}
                  className="p-2 border-t-2 text-center font-black text-base"
                  style={{
                    background: heatmapColor(v),
                    color: textColorFor(v),
                    borderTopColor: darkMode ? 'rgba(255,255,255,.3)' : '#94a3b8',
                  }}
                >
                  {v !== null ? v.toFixed(1) : '—'}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <div className={`px-5 pb-4 text-[10px] flex items-center gap-3 flex-wrap ${darkMode ? 'text-white/40' : 'text-slate-500'}`}>
        <AlertCircle className="w-3 h-3" />
        <span>Colour = score on 0-10 scale (red=weak · green=strong). Tier badges assigned by composite rank.</span>
        <span className="ml-auto flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ background: heatmapColor(2) }} /><span>Weak</span>
          <span className="w-3 h-3 rounded ml-2" style={{ background: heatmapColor(5) }} /><span>Avg</span>
          <span className="w-3 h-3 rounded ml-2" style={{ background: heatmapColor(8) }} /><span>Strong</span>
        </span>
      </div>
    </div>
  );
}
